import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, IconButton, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import { FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import TablePagination from "../../common/tablePagination";
import { listJobsApi, errorMessage } from "./api";
import { EmailTable, StatusChip, formatDateTime, useFeedback } from "./common";

const PAGE_SIZE = 10;
const REFRESH_MS = 5000;
const CHANNEL_LABELS = { EMAIL: "Email", SMS: "SMS", WHATSAPP: "WhatsApp" };
const GROUP_LABELS = { inprogress: "In progress", paid: "Paid" };

// Who a job was sent to: the uploaded file, or the course and group.
export const audienceText = (j) => (j.mode === "COURSE"
  ? `${j.audience?.coursename || `Course ${j.audience?.courseid}`} · ${GROUP_LABELS[j.audience?.group] || j.audience?.group}`
  : j.filename || "Uploaded file");

export const channelLabel = (channel) => CHANNEL_LABELS[channel] || channel;

// Send history: jobs of every channel, newest first.
export default function JobList() {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const project = Cookies.get("mhet_mst_project");
  const [channel, setChannel] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, rows: [] });
  const timer = useRef(null);

  const load = async (quiet = false) => {
    try {
      setData(await listJobsApi({ channel: channel || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }));
    } catch (err) {
      if (!quiet) feedback.failed(errorMessage(err, "Could not load the send history"));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [channel, page]);

  // While a job on this page is sending, refresh its counts.
  useEffect(() => {
    clearTimeout(timer.current);
    if (data.rows?.some((j) => j.status === "RUNNING")) timer.current = setTimeout(() => load(true), REFRESH_MS);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [data]);

  const columns = [
    { name: "Job ID", accessor: "jobid" },
    { name: "Channel", render: (j) => channelLabel(j.channel) },
    { name: "Template", align: "left", render: (j) => <>{j.templatename}<Typography variant="caption" sx={{ display: "block", color: "#777" }}>{j.templatecode} · v{j.version}</Typography></> },
    { name: "Sent To", align: "left", render: (j) => audienceText(j) },
    { name: "Status", render: (j) => <StatusChip status={j.status} /> },
    { name: "Total", accessor: "totalrows" },
    { name: "Sent", render: (j) => <b style={{ color: "rgba(34,160,107,1)" }}>{j.sent}</b> },
    { name: "Skipped", render: (j) => j.skipped },
    { name: "Failed", render: (j) => (j.failed ? <b style={{ color: "rgba(200,40,30,1)" }}>{j.failed}</b> : 0) },
    { name: "Sent On", render: (j) => formatDateTime(j.createddate) },
    {
      name: "Action",
      render: (j) => (
        <Tooltip title="View">
          <IconButton size="small" onClick={() => navigate(`/${project}/send-history?page=detail&jobid=${j.jobid}`)}><FaEye size={18} /></IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Grid container rowSpacing={2}>
      <Grid item xs={12} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography sx={{ fontWeight: "bold" }}>Channel</Typography>
        <Select size="small" displayEmpty value={channel} onChange={(e) => { setChannel(e.target.value); setPage(1); }} sx={{ minWidth: 200, backgroundColor: "#fff" }}>
          <MenuItem value="">All channels</MenuItem>
          {Object.entries(CHANNEL_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </Select>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" sx={{ color: "#666" }}>{data.total} job{data.total === 1 ? "" : "s"}</Typography>
      </Grid>
      <Grid item xs={12}>
        <EmailTable columns={columns} rows={data.rows} emptyText="Nothing sent yet" />
        {data.total > PAGE_SIZE && <TablePagination pageCount={Math.ceil(data.total / PAGE_SIZE)} currentPage={page} handlePageChange={(e, v) => setPage(v)} />}
      </Grid>
    </Grid>
  );
}
